import React from 'react'
import styled from 'styled-components'
import { Column } from 'primereact/column'
import { MdAssignment } from 'react-icons/md'

import Colors from '../../commons/Colors'
import Table from '../../commons/components/Table'
import BasicPage from '../../commons/components/BasicPage'
import NoRecordsFoundComponent from '../../commons/components/feedback/NoRecordsFoundComponent'

import { dateFormat } from '../../commons/DateUtils'
import { numberFormat } from '../../commons/NumberUtils'

import OrderItemsExpansion from './OrderItemsExpansion'
import DetailedOrderReportTotals from './DetailedOrderReportTotals'
import DetailedOrderReportFilters from './DetailedOrderReportFilters'
import { ROWS_PER_PAGE_OPTIONS } from './DetailedOrderReportContainer'

export const EMPTY_RESULT_MESSAGE =
    'Nenhum pedido encontrado para os filtros informados.'
export const INITIAL_STATE_MESSAGE =
    'Informe o período desejado e clique em Consultar para gerar o relatório.'

export default function DetailedOrderReportView({
    period,
    periodError,
    urgency,
    setUrgency,
    selectedSellers,
    setSelectedSellers,
    selectedClients,
    setSelectedClients,
    isLoading,
    isExporting,
    hasSearched,
    orders,
    totals,
    totalRecords,
    first,
    rows,
    expandedRows,
    setExpandedRows,
    handlePeriodChange,
    handleSearch,
    handlePagination,
    handleExport,
    handleClearFilters,
    fetchSellers,
    fetchClients,
}) {
    const renderDateColumn = order => dateFormat(order.integrationDate)

    // Pedido com um único produto mostra o produto na própria linha; com mais
    // de um, a linha resume e os itens aparecem na expansão (o pedido continua
    // sendo uma linha só).
    const renderProductColumn = order => {
        if (order.items?.length === 1) return order.items[0].productName

        return (
            <MultipleItemsLabel>
                {`${order.items?.length || 0} produtos`}
            </MultipleItemsLabel>
        )
    }

    const renderClassificationColumn = order => {
        if (order.items?.length === 1)
            return order.items[0].classificationName || '-'

        return '-'
    }

    const renderWeightColumn = order =>
        numberFormat(order.weightInTonnes ?? 0, 2)

    const renderTotalValueColumn = order =>
        numberFormat(order.totalValue ?? 0, 2, 'R$ ')

    const renderUrgentColumn = order => (order.isUrgent ? 'Sim' : 'Não')

    const renderRowClassName = order => ({ 'urgent-row': !!order.isUrgent })

    const renderEmptyState = () => (
        <NoRecordsFoundComponent
            renderIcon={() => <MdAssignment size={60} />}
            renderText={() =>
                hasSearched ? EMPTY_RESULT_MESSAGE : INITIAL_STATE_MESSAGE
            }
        />
    )

    return (
        <BasicPage title={'Relatório Detalhado de Pedidos'}>
            <Content>
                <DetailedOrderReportFilters
                    period={period}
                    periodError={periodError}
                    urgency={urgency}
                    setUrgency={setUrgency}
                    selectedSellers={selectedSellers}
                    setSelectedSellers={setSelectedSellers}
                    selectedClients={selectedClients}
                    setSelectedClients={setSelectedClients}
                    isLoading={isLoading}
                    isExporting={isExporting}
                    handlePeriodChange={handlePeriodChange}
                    handleSearch={handleSearch}
                    handleExport={handleExport}
                    handleClearFilters={handleClearFilters}
                    fetchSellers={fetchSellers}
                    fetchClients={fetchClients}
                />

                <DetailedOrderReportTotals totals={totals} />

                {!isLoading && !orders.length ? (
                    renderEmptyState()
                ) : (
                    <ReportTable
                        value={orders}
                        loading={isLoading}
                        dataKey="orderId"
                        onPage={handlePagination}
                        rows={rows}
                        first={first}
                        totalRecords={totalRecords}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        totalRecordsText="{totalRecords} Pedidos Encontrados"
                        expandedRows={expandedRows}
                        onRowToggle={event => setExpandedRows(event.data)}
                        rowExpansionTemplate={order => (
                            <OrderItemsExpansion order={order} />
                        )}
                        rowClassName={renderRowClassName}
                        emptyMessage={EMPTY_RESULT_MESSAGE}
                    >
                        <Column expander style={expanderColumn} />
                        <Column
                            header={'Número do pedido'}
                            field={'number'}
                            style={centerAlignColumn}
                        />
                        <Column
                            header={'Data do pedido'}
                            field={'integrationDate'}
                            body={renderDateColumn}
                            style={centerAlignColumn}
                        />
                        <Column
                            header={'Vendedor'}
                            field={'sellerName'}
                            style={startAlignColumn}
                        />
                        <Column
                            header={'Cliente'}
                            field={'clientName'}
                            style={startAlignColumn}
                        />
                        <Column
                            header={'Produto'}
                            body={renderProductColumn}
                            style={startAlignColumn}
                        />
                        <Column
                            header={'Peso (ton)'}
                            body={renderWeightColumn}
                            style={centerAlignColumn}
                        />
                        <Column
                            header={'Classificação'}
                            body={renderClassificationColumn}
                            style={startAlignColumn}
                        />
                        <Column
                            header={'Valor total'}
                            body={renderTotalValueColumn}
                            style={centerAlignColumn}
                        />
                        <Column
                            header={'Urgente'}
                            body={renderUrgentColumn}
                            style={centerAlignColumn}
                        />
                    </ReportTable>
                )}
            </Content>
        </BasicPage>
    )
}

const Content = styled.div`
    padding: 16px 24px;
`

const ReportTable = styled(Table)`
    .urgent-row > td:first-child {
        border-left: solid 3px ${Colors.orange} !important;
    }
`

const MultipleItemsLabel = styled.span`
    font-style: italic;
    color: ${Colors.brownishGrey};
`

const centerAlignColumn = {
    textAlign: 'center',
}

const startAlignColumn = {
    textAlign: 'start',
}

const expanderColumn = {
    width: '3em',
    textAlign: 'center',
}
